import dayjs from "dayjs";

// Monthly Budget P&L shared types/helpers. Modeled on the URI BUDGET
// "F-1 Top Sheet-P&L": rows = inflow/outflow heads, columns = months.
// Placeholder heads (Escalation, staff Manpower, Statutory) arrive as '0.00'
// until the overhead data push lands — render muted, never clickable.

import { num as toNum } from "../ThreeMConsolidation/threeMShared";

export {
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
  perUnit,
} from "../ThreeMConsolidation/threeMShared";

export interface PnlMonthColumn {
  month: string; // 'YYYY-MM-01' | 'TOTAL'
  inflowAmount: string;
  escalationAmount: string;
  totalInflow: string;
  salariesAmount: string;
  machineryAmount: string;
  materialAmount: string;
  subconPrwAmount: string;
  statutoryAmount: string;
  totalOutflow: string;   // PLANNED outflow
  margin: string;
  marginPct: string;
  scheduledTaskCount: number;
  actualOutflow: string;  // '0.00' until the actuals feed lands
}

export interface PnlUnscheduledRow {
  taskId: number;
  taskName: string;
  taskSequenceId: string;
  phaseId: number;
  phaseName: string;
  activityId: number;
  activityName: string;
  reason: "NO_SCHEDULE" | "NO_WORK_QTY" | string;
  invisibleInflow: string;
  materialAmount: string;
  manpowerAmount: string;
  machineryAmount: string;
  totalOutflow: string;
}

export interface PnlTopSheetData {
  projectName?: string;
  scheduleRange?: { firstMonth?: string; lastMonth?: string };
  months?: PnlMonthColumn[];
  totals?: PnlMonthColumn;
  unscheduled?: {
    taskCount: number;
    invisibleInflow: string;
    materialAmount: string;
    manpowerAmount: string;
    machineryAmount: string;
    totalOutflow: string;
    rows: PnlUnscheduledRow[];
  };
}

export interface PnlTaskRow {
  phaseId: number;
  phaseName: string;
  activityId: number;
  activityName: string;
  taskId: number;
  taskName: string;
  taskSequenceId: string;
  workQuantity: string;
  workUom: string;
  /** Basic Rate — the task rate net of GST and labour cess. */
  rate: string;
  scheduledQty: string;
  monthShare: string;
  inflowAmount: string;
  materialAmount: string;
  manpowerAmount: string;
  machineryAmount: string;
  totalOutflow: string;
  margin: string;
}

export type PnlRowKind =
  | "inflow"
  | "escalation"
  | "totalInflow"
  | "salaries"
  | "machinery"
  | "material"
  | "subconPrw"
  | "statutory"
  | "totalOutflow"
  | "actualOutflow"
  | "margin"
  | "marginPct";

export type PnlDetailTab = "tasks" | "materials" | "manpower" | "machinery";

export const kindToTab = (kind: PnlRowKind): PnlDetailTab => {
  switch (kind) {
    case "material":
      return "materials";
    case "subconPrw":
      return "manpower";
    case "machinery":
      return "machinery";
    default:
      return "tasks";
  }
};

export const fmtMonth = (month: string) => dayjs(month).format("MMM YY");
export const fmtMonthLong = (month: string) => dayjs(month).format("MMMM YYYY");

export const PNL_COLORS = {
  material: "#1677ff",
  manpower: "#389e0d",
  machinery: "#fa8c16",
  inflow: "#022c57",
  margin: "#722ed1",
  negative: "#cf1322",
  positive: "#389e0d",
};

// Indian fiscal year (Apr→Mar). fyStartYear 2026 = Apr 2026 … Mar 2027.
export interface FyOption {
  label: string;
  fromMonth: string; // 'YYYY-04-01'
  toMonth: string;   // 'YYYY+1-03-01'
}

export const fyOf = (month: string): number => {
  const d = dayjs(month);
  return d.month() >= 3 ? d.year() : d.year() - 1; // month() is 0-based; Apr = 3
};

export const fyOption = (fyStartYear: number): FyOption => ({
  label: `FY ${fyStartYear}-${String((fyStartYear + 1) % 100).padStart(2, "0")}`,
  fromMonth: `${fyStartYear}-04-01`,
  toMonth: `${fyStartYear + 1}-03-01`,
});

// FY options spanning the project's scheduled range (falls back to the
// current FY when the project has no schedules yet).
export const buildFyOptions = (firstMonth?: string, lastMonth?: string): FyOption[] => {
  const currentFy = fyOf(dayjs().format("YYYY-MM-01"));
  const startFy = firstMonth ? fyOf(firstMonth) : currentFy;
  const endFy = lastMonth ? fyOf(lastMonth) : currentFy;
  const options: FyOption[] = [];
  for (let year = startFy; year <= endFy; year++) options.push(fyOption(year));
  return options;
};

export const sumColumns = (columns: PnlMonthColumn[], key: keyof PnlMonthColumn): number =>
  columns.reduce((total, column) => total + toNum(column[key] as string), 0);
